/* =====================================================================
   Game Day Collective — theme.js
   Store functionality works without the animation libraries; GSAP,
   ScrollTrigger, Flip and Lenis only add motion when they are available.
   ===================================================================== */
(() => {
  'use strict';

  const $ = (s, c = document) => c.querySelector(s);
  const $$ = (s, c = document) => Array.from(c.querySelectorAll(s));
  const GDC = window.GDC || {};
  const STR = GDC.strings || {};
  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const DESIGN = !!(GDC.designMode || (window.Shopify && window.Shopify.designMode));
  const MOTION = !REDUCED && !(GDC.motion && GDC.motion.reveal === false);
  const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
  const gs = () => (window.gsap && !REDUCED ? window.gsap : null);
  const hasST = () => !!(gs() && window.ScrollTrigger);

  if (window.gsap) {
    const plugins = [window.ScrollTrigger, window.Flip].filter(Boolean);
    if (plugins.length) window.gsap.registerPlugin(...plugins);
  }

  /* ---------------- helpers ---------------- */
  function formatMoney(cents, format) {
    format = format || GDC.moneyFormat || '${{amount}}';
    if (typeof cents === 'string') cents = cents.replace('.', '');
    cents = parseInt(cents, 10) || 0;
    const fmt = (n, decimals, thou = ',', dec = '.') => {
      const [i, f] = (n / 100).toFixed(decimals).split('.');
      return i.replace(/\B(?=(\d{3})+(?!\d))/g, thou) + (f ? dec + f : '');
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (m, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(cents, 0);
        case 'amount_with_comma_separator': return fmt(cents, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(cents, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(cents, 2, "'", '.');
        case 'amount_with_space_separator': return fmt(cents, 2, ' ', ',');
        default: return fmt(cents, 2);
      }
    });
  }

  let toastTimer;
  function toast(msg) {
    const t = $('[data-toast]');
    if (!t || !msg) return;
    t.textContent = msg;
    t.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove('is-on'), 2600);
  }

  function sectionIdOf(el) {
    const s = el && el.closest('.shopify-section');
    return s ? s.id.replace('shopify-section-', '') : null;
  }

  /* ---------------- smooth scroll ---------------- */
  let lenis = null;
  function initLenis() {
    if (!(GDC.motion && GDC.motion.smooth) || REDUCED || DESIGN || !window.Lenis) return;
    lenis = new window.Lenis({
      lerp: 0.1,
      wheelMultiplier: 0.95,
      prevent: (node) => !!(node && node.closest && node.closest('[data-lenis-prevent], .gd-drawer, .gd-search, .gd-mnav, .gd-vmodal, .gd-lightbox, .gd-filter__panel, .gd-qa__pop'))
    });
    if (hasST()) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.add((t) => lenis.raf(t * 1000));
      window.gsap.ticker.lagSmoothing(0);
    } else {
      const raf = (t) => { lenis.raf(t); requestAnimationFrame(raf); };
      requestAnimationFrame(raf);
    }
  }
  let lockCount = 0;
  function lockScroll(on) {
    lockCount = Math.max(0, lockCount + (on ? 1 : -1));
    const locked = lockCount > 0;
    document.documentElement.style.overflow = locked ? 'hidden' : '';
    if (lenis) locked ? lenis.stop() : lenis.start();
  }
  function scrollToEl(el) {
    if (!el) return;
    if (lenis) lenis.scrollTo(el, { offset: -10, duration: 1.4 });
    else el.scrollIntoView({ behavior: REDUCED ? 'auto' : 'smooth', block: 'start' });
  }
  document.addEventListener('click', (e) => {
    const a = e.target.closest('a[href^="#"]');
    if (!a || a.getAttribute('href').length < 2 || a.hasAttribute('data-toggle-recover')) return;
    const target = document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)));
    if (!target) return;
    e.preventDefault();
    closeMobileNav();
    scrollToEl(target);
  });

  /* ---------------- split headings into words ---------------- */
  function splitWords(el) {
    if (el.dataset.split) return;
    el.dataset.split = '1';
    const walk = (node) => {
      Array.from(node.childNodes).forEach((c) => {
        if (c.nodeType === 3) {
          const frag = document.createDocumentFragment();
          c.textContent.split(/(\s+)/).forEach((t) => {
            if (!t) return;
            if (/^\s+$/.test(t)) { frag.append(' '); return; }
            const w = document.createElement('span');
            w.className = 'w';
            const wi = document.createElement('span');
            wi.className = 'wi';
            wi.textContent = t;
            w.append(wi);
            frag.append(w);
          });
          c.replaceWith(frag);
        } else if (c.nodeType === 1 && c.tagName !== 'BR' && !c.classList.contains('w')) {
          walk(c);
        }
      });
    };
    walk(el);
  }

  function revealIn(root) {
    const g = gs();
    if (!g || !hasST() || !MOTION) return;
    $$('.gd-split', root).forEach((el) => {
      splitWords(el);
      g.from($$('.wi', el), { yPercent: 115, rotation: 3, duration: 1.1, stagger: 0.06, ease: 'power4.out', scrollTrigger: { trigger: el, start: 'top 90%', once: true } });
    });
    $$('[data-reveal]', root).forEach((el) => {
      g.from(el, { y: 70, opacity: 0, rotation: +(el.dataset.revealRot || 0), duration: 1.1, ease: 'power4.out', clearProps: 'opacity', scrollTrigger: { trigger: el, start: 'top 92%', once: true } });
    });
    $$('.gd-grid .gd-card, .gd-fav__grid .gd-card', root).forEach((el) => {
      g.from(el, { y: 80, opacity: 0, duration: 1.1, ease: 'power4.out', clearProps: 'opacity,transform', scrollTrigger: { trigger: el, start: 'top 95%', once: true } });
    });
  }

  /* ---------------- marquees + scroll velocity ---------------- */
  const marquees = [];
  let vel = 0;
  let lastY = window.scrollY;
  function addMarquee(el, speed, opts = {}) {
    if (!el || REDUCED || !speed || el.dataset.mqInit) return;
    el.dataset.mqInit = '1';
    let unit;
    if (opts.row) {
      unit = () => el.scrollWidth / 2;
    } else {
      const first = el.firstElementChild;
      if (!first) return;
      for (let i = 0; i < 4; i++) {
        const c = first.cloneNode(true);
        c.setAttribute('aria-hidden', 'true');
        el.appendChild(c);
      }
      unit = () => first.offsetWidth;
    }
    marquees.push({ el, s: speed, unit, x: 0, skew: !!opts.skew });
  }
  const tickers = [];
  function frame(t) {
    const y = window.scrollY;
    vel += (y - lastY - vel) * 0.2;
    lastY = y;
    for (const m of marquees) {
      if (!m.el.isConnected) continue;
      const w = m.unit();
      if (!w) continue;
      const dir = m.skew && vel < -0.5 ? -1 : 1;
      m.x += m.s * (1 + Math.min(Math.abs(vel), 60) * 0.08) * dir;
      if (m.x <= -w) m.x += w;
      if (m.x > 0) m.x -= w;
      m.el.style.transform = `translate3d(${m.x}px,0,0)` + (m.skew ? ` skewX(${clamp(-vel * 0.5, -14, 14)}deg)` : '');
    }
    for (const fn of tickers) fn(t / 1000);
    requestAnimationFrame(frame);
  }
  requestAnimationFrame(frame);

  /* ---------------- cursor + magnetic + tilt ---------------- */
  function initCursor() {
    const cur = $('.gd-cur');
    if (!cur || !FINE || REDUCED) return;
    document.documentElement.classList.add('has-cursor');
    const tx = $('.tx', cur);
    let x = -100, y = -100, cx = -100, cy = -100;
    window.addEventListener('mousemove', (e) => { x = e.clientX; y = e.clientY; }, { passive: true });
    tickers.push(() => {
      cx += (x - cx) * 0.3;
      cy += (y - cy) * 0.3;
      cur.style.transform = `translate3d(${cx}px,${cy}px,0)`;
    });
    document.addEventListener('mouseover', (e) => {
      const t = e.target.closest('[data-cursor]');
      if (t && t.dataset.cursor) { tx.textContent = t.dataset.cursor; cur.classList.add('on'); }
      else cur.classList.remove('on');
    });
    document.addEventListener('mouseleave', () => { x = -100; y = -100; });
  }
  function initMagnetic(root) {
    if (!FINE || REDUCED) return;
    $$('[data-mag]', root).forEach((b) => {
      if (b.dataset.magInit) return;
      b.dataset.magInit = '1';
      b.addEventListener('mousemove', (e) => {
        const r = b.getBoundingClientRect();
        const dx = (e.clientX - r.left - r.width / 2) * 0.3;
        const dy = (e.clientY - r.top - r.height / 2) * 0.4;
        const g = gs();
        if (g) g.to(b, { x: dx, y: dy, duration: 0.5, ease: 'power3.out' });
        else b.style.transform = `translate(${dx}px,${dy}px)`;
      });
      b.addEventListener('mouseleave', () => {
        const g = gs();
        if (g) g.to(b, { x: 0, y: 0, duration: 0.8, ease: 'elastic.out(1,.5)' });
        else b.style.transform = '';
      });
    });
  }
  function initTilt(root) {
    const g = gs();
    if (!FINE || !g) return;
    $$('.gd-card', root).forEach((card) => {
      const media = $('.gd-card__media', card);
      if (!media || card.dataset.tilt) return;
      card.dataset.tilt = '1';
      card.addEventListener('mousemove', (e) => {
        const r = card.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        g.to(media, { rotateY: px * 9, rotateX: -py * 9, transformPerspective: 900, duration: 0.5, ease: 'power2.out' });
      });
      card.addEventListener('mouseleave', () => g.to(media, { rotateX: 0, rotateY: 0, duration: 0.8, ease: 'elastic.out(1,.5)' }));
    });
  }

  /* ---------------- header ---------------- */
  let navLastY = window.scrollY;
  function onScrollHeader() {
    const nav = $('#GdNav');
    if (!nav) return;
    const y = window.scrollY;
    const promo = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--promo-h')) || 36;
    nav.classList.toggle('is-stuck', y > promo);
    const d = y - navLastY;
    if (Math.abs(d) > 6) {
      nav.classList.toggle('is-hidden', d > 0 && y > 420 && !document.body.classList.contains('drawer-open'));
      navLastY = y;
    }
  }
  let mnavOpener = null;
  function openMobileNav(btn) {
    const m = $('[data-mnav]');
    if (!m) return;
    mnavOpener = btn;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    if (btn) btn.setAttribute('aria-expanded', 'true');
    lockScroll(true);
    setTimeout(() => { const c = $('[data-mnav-close]', m); if (c) c.focus(); }, 60);
  }
  function closeMobileNav() {
    const m = $('[data-mnav]');
    if (!m || !m.classList.contains('is-open')) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    $$('[data-mnav-open]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
    lockScroll(false);
    if (mnavOpener) mnavOpener.focus();
  }
  /* search overlay: school shortcuts + live predictive results */
  let searchOpener = null;
  function openSearch(btn) {
    const s = $('[data-search]');
    if (!s) return;
    searchOpener = btn || null;
    s.classList.add('is-open');
    s.setAttribute('aria-hidden', 'false');
    lockScroll(true);
    setTimeout(() => { const i = $('[data-search-input]', s); if (i) i.focus(); }, 80);
  }
  function closeSearch() {
    const s = $('[data-search]');
    if (!s || !s.classList.contains('is-open')) return;
    s.classList.remove('is-open');
    s.setAttribute('aria-hidden', 'true');
    lockScroll(false);
    if (searchOpener) searchOpener.focus();
  }
  (function initPredictive() {
    const s = $('[data-search]');
    if (!s) return;
    const input = $('[data-search-input]', s);
    const results = $('[data-search-results]', s);
    const defaults = $('[data-search-defaults]', s);
    const quick = $('[data-quick-group]', s);
    const schoolGroup = $('[data-school-group]', s);
    const chips = $$('.gd-school-chip[data-keys]', s);
    const body = $('.gd-search__body', s);
    const url = s.dataset.psUrl || '/search/suggest';
    let timer = null;
    let ctrl = null;
    const cache = {};
    const filterSchools = (q) => {
      let matches = 0;
      chips.forEach((c) => {
        const hit = !q || c.dataset.keys.split(' ').some((k) => k.startsWith(q)) || c.dataset.keys.includes(q);
        c.hidden = !hit;
        c.classList.toggle('is-match', !!q && hit);
        if (hit) matches++;
      });
      if (schoolGroup) schoolGroup.hidden = matches === 0;
      return matches;
    };
    const show = (html) => {
      results.innerHTML = html;
      const colsGroup = $('[data-ps-cols]', results);
      if (!colsGroup) return;
      const shown = new Set($$('.gd-school-chip[data-keys]:not([hidden])', s).map((c) => c.getAttribute('href')));
      $$('a', colsGroup).forEach((a) => { if (shown.has(a.getAttribute('href'))) a.remove(); });
      if (!$('a', colsGroup)) colsGroup.remove();
    };
    const fetchResults = (q) => {
      if (cache[q]) { show(cache[q]); return; }
      if (ctrl) ctrl.abort();
      ctrl = 'AbortController' in window ? new AbortController() : null;
      body.classList.add('is-loading');
      const params = new URLSearchParams({ q, section_id: 'predictive-search' });
      params.append('resources[type]', 'product,collection,query,page');
      params.append('resources[limit]', '6');
      params.append('resources[options][unavailable_products]', 'last');
      params.append('resources[options][fields]', 'title,product_type,variants.title,tag,vendor');
      fetch(`${url}?${params}`, ctrl ? { signal: ctrl.signal } : {})
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = $('[data-ps-results]', doc);
          const out = fresh ? fresh.outerHTML : '';
          cache[q] = out;
          if (input.value.trim().toLowerCase() === q) show(out);
        })
        .catch(() => {})
        .finally(() => body.classList.remove('is-loading'));
    };
    input.addEventListener('input', () => {
      const q = input.value.trim().toLowerCase();
      filterSchools(q);
      if (quick) quick.hidden = !!q;
      clearTimeout(timer);
      if (!q) { show(''); return; }
      timer = setTimeout(() => fetchResults(q), 220);
    });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') {
        const first = $('[data-ps-item], .gd-school-chip:not([hidden])', s);
        if (first) { e.preventDefault(); first.focus(); }
      }
    });
    s.addEventListener('keydown', (e) => {
      if (!['ArrowDown', 'ArrowUp'].includes(e.key) || e.target === input) return;
      const items = $$('.gd-school-chip:not([hidden]), [data-ps-item], [data-ps-all]', s).filter((el) => el.offsetParent);
      const i = items.indexOf(e.target);
      if (i < 0) return;
      e.preventDefault();
      if (e.key === 'ArrowUp' && i === 0) { input.focus(); return; }
      const next = items[Math.min(items.length - 1, Math.max(0, i + (e.key === 'ArrowDown' ? 1 : -1)))];
      if (next) next.focus();
    });
    if (defaults) defaults.hidden = false;
  })();
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (t.closest('[data-mnav-open]')) { e.preventDefault(); openMobileNav(t.closest('[data-mnav-open]')); return; }
    if (t.closest('[data-mnav-close]')) { e.preventDefault(); closeMobileNav(); return; }
    if (t.closest('[data-mnav] a')) closeMobileNav();
    if (t.closest('[data-search-open]')) { e.preventDefault(); openSearch(t.closest('[data-search-open]')); return; }
    if (t.closest('[data-search-close]') || t.matches('[data-search]')) { e.preventDefault(); closeSearch(); return; }
  });

  /* ---------------- cart ---------------- */
  const Cart = {
    drawer() { return $('[data-cart-drawer]'); },
    opener: null,
    open() {
      const d = this.drawer();
      if (GDC.cartType !== 'drawer' || !d) { window.location.href = GDC.routes.cart; return; }
      this.opener = document.activeElement;
      d.classList.add('is-open');
      d.setAttribute('aria-hidden', 'false');
      document.body.classList.add('drawer-open');
      lockScroll(true);
      setTimeout(() => { const p = $('[data-drawer-panel]', d); if (p) p.focus(); }, 60);
    },
    close() {
      const d = this.drawer();
      if (!d || !d.classList.contains('is-open')) return;
      d.classList.remove('is-open');
      d.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('drawer-open');
      lockScroll(false);
      if (this.opener && this.opener.focus) this.opener.focus();
    },
    setCount(n, bump = true) {
      $$('[data-cart-count]').forEach((el) => { el.textContent = n; });
      if (bump) $$('.gd-cart-btn').forEach((b) => { b.classList.remove('bump'); void b.offsetWidth; b.classList.add('bump'); });
    },
    renderDrawer(html) {
      if (!html) return null;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-cart-drawer]', doc);
      const cur = this.drawer();
      if (fresh && cur) {
        const curPanel = $('[data-drawer-panel]', cur);
        const freshPanel = $('[data-drawer-panel]', fresh);
        const curBody = $('.gd-drawer__body', curPanel);
        const scroll = curBody ? curBody.scrollTop : 0;
        // swap the pieces individually so the panel never flashes
        ['.gd-drawer__head h2', '[data-drawer-promo]', '.gd-drawer__body'].forEach((sel) => {
          const a = $(sel, curPanel);
          const b = $(sel, freshPanel);
          if (a && b) a.innerHTML = b.innerHTML;
        });
        const curFoot = $('.gd-drawer__foot', curPanel);
        const freshFoot = $('.gd-drawer__foot', freshPanel);
        if (curFoot && freshFoot) curFoot.innerHTML = freshFoot.innerHTML;
        else if (curFoot && !freshFoot) curFoot.remove();
        else if (!curFoot && freshFoot) curPanel.append(freshFoot);
        const body = $('.gd-drawer__body', curPanel);
        if (body) body.scrollTop = scroll;
        cur.dataset.count = fresh.dataset.count;
      }
      return fresh ? +fresh.dataset.count : null;
    },
    renderSection(id, html) {
      const target = document.getElementById('shopify-section-' + id);
      if (!target || !html) return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.getElementById('shopify-section-' + id);
      if (fresh) target.innerHTML = fresh.innerHTML;
    },
    async add(formData) {
      // formData: a FormData, or an array of line items ({ id, quantity, properties })
      const isItems = Array.isArray(formData);
      let body;
      const headers = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };
      if (isItems) {
        headers['Content-Type'] = 'application/json';
        body = JSON.stringify({ items: formData, sections: 'cart-drawer', sections_url: window.location.pathname });
      } else {
        formData.append('sections', 'cart-drawer');
        formData.append('sections_url', window.location.pathname);
        body = formData;
      }
      const res = await fetch(GDC.routes.cartAdd + '.js', { method: 'POST', headers, body });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || STR.error);
      const html = data.sections && data.sections['cart-drawer'];
      const count = this.renderDrawer(html);
      if (count !== null) this.setCount(count);
      if (isItems && data.items && data.items.length) data.title = (data.items.find((i) => i.id === formData[0].id) || data.items[0]).product_title;
      document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      this.reconcile(html).catch(() => {});
      return data;
    },
    // extra-letter add-ons must follow their necklace's quantity; Liquid lists any line that's out of step
    async reconcile(html) {
      let node;
      if (html) node = $('[data-cart-fix]', new DOMParser().parseFromString(html, 'text/html'));
      else node = $('[data-cart-fix]');
      if (!node || this.fixing) return;
      let updates;
      try { updates = JSON.parse(node.textContent); } catch (e) { return; }
      if (!updates || !Object.keys(updates).length) return;
      this.fixing = true;
      try { await this.update(updates); } finally { this.fixing = false; }
    },
    async update(updates, lineEl) {
      const pageSection = $('[data-cart-page]') ? sectionIdOf($('[data-cart-page]')) : null;
      const sections = ['cart-drawer'];
      if (pageSection) sections.push(pageSection);
      const res = await fetch(GDC.routes.cartUpdate + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: sections.join(','), sections_url: window.location.pathname })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status) throw new Error(data.description || data.message || STR.error);
      const count = this.renderDrawer(data.sections && data.sections['cart-drawer']);
      if (pageSection && data.sections) this.renderSection(pageSection, data.sections[pageSection]);
      this.setCount(count !== null ? count : data.item_count, false);
      return data;
    },
    async change(line, quantity, lineEl) {
      const pageSection = $('[data-cart-page]') ? sectionIdOf($('[data-cart-page]')) : null;
      const sections = ['cart-drawer'];
      if (pageSection) sections.push(pageSection);
      if (lineEl) lineEl.classList.add('is-updating');
      const extraEl = lineEl && lineEl.dataset.link && lineEl.dataset.key ? $('[data-extra-key]', lineEl) : null;
      if (extraEl) {
        try {
          await this.update({ [lineEl.dataset.key]: quantity, [extraEl.dataset.extraKey]: quantity * (+extraEl.dataset.per || 0) }, lineEl);
        } catch (err) {
          if (lineEl.isConnected) {
            lineEl.classList.remove('is-updating');
            const errEl = $('[data-line-err]', lineEl);
            if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
          } else toast(err.message);
        }
        return;
      }
      try {
        const res = await fetch(GDC.routes.cartChange + '.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line, quantity, sections: sections.join(','), sections_url: window.location.pathname })
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || STR.error);
        const count = this.renderDrawer(data.sections && data.sections['cart-drawer']);
        if (pageSection && data.sections) this.renderSection(pageSection, data.sections[pageSection]);
        this.setCount(count !== null ? count : data.item_count, false);
        this.reconcile(data.sections && data.sections['cart-drawer']).catch(() => {});
      } catch (err) {
        if (lineEl && lineEl.isConnected) {
          lineEl.classList.remove('is-updating');
          const errEl = $('[data-line-err]', lineEl);
          if (errEl) { errEl.textContent = err.message; errEl.hidden = false; }
        } else toast(err.message);
      }
    },
    queue: {},
    changeSoon(line, quantity, lineEl) {
      clearTimeout(this.queue[line]);
      this.queue[line] = setTimeout(() => { delete this.queue[line]; this.change(line, quantity, lineEl); }, 320);
    }
  };
  window.GDCart = Cart;

  function flyStar(fromEl) {
    const g = gs();
    const to = $('.gd-cart-btn');
    if (!g || !fromEl || !to) return;
    const a = fromEl.getBoundingClientRect();
    const b = to.getBoundingClientRect();
    const s = document.createElement('span');
    s.className = 'gd-conf';
    s.style.color = 'var(--rust)';
    s.style.width = s.style.height = '28px';
    document.body.append(s);
    g.fromTo(s, { x: a.left + a.width / 2 - 14, y: a.top + a.height / 2 - 14, scale: 0.4 }, { x: b.left + b.width / 2 - 14, y: b.top + b.height / 2 - 14, scale: 1.2, rotation: 360, duration: 0.9, ease: 'power3.inOut', onComplete: () => s.remove() });
  }

  document.addEventListener('click', (e) => {
    const t = e.target;
    const openBtn = t.closest('[data-cart-open]');
    if (openBtn && GDC.cartType === 'drawer' && Cart.drawer() && !document.body.classList.contains('template-cart')) {
      e.preventDefault();
      closeMobileNav();
      Cart.open();
      return;
    }
    if (t.closest('[data-drawer-close]')) { e.preventDefault(); Cart.close(); return; }

    const qtyBtn = t.closest('[data-qty-btn]');
    if (qtyBtn) {
      const box = qtyBtn.closest('[data-qty]');
      const input = box && $('input', box);
      if (!input) return;
      const min = input.hasAttribute('data-line-qty') ? 0 : +(input.min || 1);
      const next = Math.max(min, (+input.value || 0) + (qtyBtn.dataset.qtyBtn === 'plus' ? 1 : -1));
      input.value = next;
      if (input.hasAttribute('data-line-qty')) Cart.changeSoon(+input.dataset.lineQty, next, qtyBtn.closest('[data-line]'));
      else input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const rm = t.closest('[data-line-remove]');
    if (rm) { e.preventDefault(); Cart.change(+rm.dataset.lineRemove, 0, rm.closest('[data-line]')); return; }

    // close open quick-add popovers when clicking elsewhere
    $$('details[data-qa][open]').forEach((d) => { if (!d.contains(t)) d.removeAttribute('open'); });
  });
  document.addEventListener('change', (e) => {
    const input = e.target.closest('[data-line-qty]');
    if (input) Cart.change(+input.dataset.lineQty, Math.max(0, +input.value || 0), input.closest('[data-line]'));
    const note = e.target.closest('[data-cart-note]');
    if (note) {
      fetch(GDC.routes.cartUpdate + '.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note: note.value }) });
    }
  });
  document.addEventListener('toggle', (e) => {
    const d = e.target;
    if (d.matches && d.matches('details[data-qa]') && d.open) {
      $$('details[data-qa][open]').forEach((o) => { if (o !== d) o.removeAttribute('open'); });
    }
  }, true);

  // color swatches on product cards
  function selectSwatch(sw) {
    const card = sw.closest('[data-card]');
    const group = sw.closest('[data-swatches]');
    if (!card || !group) return;
    $$('.gd-swatch', group).forEach((b) => b.setAttribute('aria-checked', b === sw ? 'true' : 'false'));
    const name = $('[data-swatch-name]', group);
    if (name) name.textContent = sw.dataset.name;
    const idIn = $('[data-card-variant]', card);
    if (idIn) idIn.value = sw.dataset.variantId;
    const avail = sw.dataset.available === 'true';
    const btn = $('[data-card-add]', card);
    if (btn) {
      btn.disabled = !avail;
      const lbl = $('[data-card-add-label]', btn);
      if (lbl) lbl.textContent = avail ? STR.addToCart : STR.soldOut;
    }
    if (sw.dataset.url) $$('.gd-card__media, .gd-card__title a, [data-card-link]', card).forEach((a) => { a.href = sw.dataset.url; });
    const price = $('[data-card-price]', card);
    if (price && sw.dataset.price) price.innerHTML = sw.dataset.price + (sw.dataset.compare ? `<s>${sw.dataset.compare}</s>` : '');
    const img = $('.gd-card__media img', card);
    if (img && sw.dataset.img && img.dataset.current !== sw.dataset.img) {
      card.classList.add('has-variant');
      img.classList.add('is-swapping');
      const pre = new Image();
      pre.onload = pre.onerror = () => {
        img.removeAttribute('srcset');
        img.src = sw.dataset.img;
        img.dataset.current = sw.dataset.img;
        requestAnimationFrame(() => img.classList.remove('is-swapping'));
      };
      pre.src = sw.dataset.img;
    }
  }
  document.addEventListener('click', (e) => {
    const sw = e.target.closest('[data-swatches] .gd-swatch');
    if (!sw) return;
    e.preventDefault();
    selectSwatch(sw);
  });
  document.addEventListener('keydown', (e) => {
    const sw = e.target.closest && e.target.closest('[data-swatches] .gd-swatch');
    if (!sw || !['ArrowRight', 'ArrowLeft'].includes(e.key)) return;
    const all = $$('.gd-swatch', sw.closest('[data-swatches]'));
    const next = all[(all.indexOf(sw) + (e.key === 'ArrowRight' ? 1 : -1) + all.length) % all.length];
    e.preventDefault();
    next.focus();
    selectSwatch(next);
  });

  /* ---------------- extra letters (Theme settings > Personalization > Extra letters) ---------------- */
  function lettersInput(form) {
    return $('[data-letters]', form) || (form.id ? $('[data-letters][form="' + form.id + '"]') : null);
  }
  function extraLetters(form) {
    const cfg = $('[data-extra-cfg][data-extra-variant]', form);
    const input = cfg && lettersInput(form);
    if (!cfg || !input) return { count: 0 };
    const letters = input.value.trim();
    const used = letters.replace(/\s/g, '').length;
    const count = Math.max(0, used - (+cfg.dataset.freeLetters || 0));
    return { count, letters, variant: +cfg.dataset.extraVariant, price: +cfg.dataset.extraPrice || 0 };
  }
  function initExtraLetters(root) {
    $$('form', root).forEach((form) => {
      const cfg = $('[data-extra-cfg][data-extra-variant]', form);
      const input = cfg && lettersInput(form);
      if (!input || input.dataset.extraReady) return;
      input.dataset.extraReady = '1';
      const scope = form.closest('[data-gd]') || document;
      const refresh = () => {
        const x = extraLetters(form);
        $$('[data-extra-note]', scope).forEach((n) => {
          n.textContent = x.count ? n.dataset.live.replace('[count]', x.count).replace('[total]', formatMoney(x.count * x.price)) : n.dataset.base;
          n.classList.toggle('is-on', x.count > 0);
        });
        form.classList.toggle('has-extra-letters', x.count > 0);
        form.dispatchEvent(new CustomEvent('extra:change', { detail: x }));
      };
      input.addEventListener('input', refresh);
      refresh();
    });
  }

  // AJAX add to cart for any form marked data-ajax-cart
  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!form.matches('form[data-ajax-cart]') || e.defaultPrevented) return;
    const extra = extraLetters(form);
    // extra letters always go through here (a plain form post can't add the add-on)
    if (!GDC.ajaxAdd && !extra.count) return;
    e.preventDefault();
    const submitter = e.submitter;
    const fd = new FormData(form);
    if (submitter && submitter.name) fd.set(submitter.name, submitter.value);
    if (!fd.get('id')) return;
    let payload = fd;
    if (extra.count) {
      const main = { id: +fd.get('id'), quantity: Math.max(1, +fd.get('quantity') || 1), properties: {} };
      for (const [k, v] of fd.entries()) {
        const m = k.match(/^properties\[(.+)\]$/);
        if (m && v !== '') main.properties[m[1]] = v;
      }
      if (fd.get('selling_plan')) main.selling_plan = fd.get('selling_plan');
      const link = Date.now().toString(36) + Math.random().toString(36).slice(2, 7);
      main.properties._gd_link = link;
      payload = [main, { id: extra.variant, quantity: extra.count * main.quantity, properties: { [STR.extraFor || 'For']: extra.letters, _gd_link: link, _gd_per: String(extra.count) } }];
    }
    const btn = submitter || $('[type=submit]', form) || $('[type=submit][form="' + form.id + '"]');
    const err = $('[data-form-error]', form) || $('[data-form-error]', form.closest('section') || document.body);
    if (err) err.textContent = '';
    const qa = form.querySelector('details[data-qa]');
    const label = btn && btn.closest('details') ? btn.closest('details').querySelector('summary') : btn;
    if (label) label.classList.add('is-loading');
    if (btn) btn.setAttribute('aria-busy', 'true');
    try {
      const data = await Cart.add(payload);
      if (!GDC.ajaxAdd) { window.location.href = GDC.routes.cart; return; }
      if (qa) qa.removeAttribute('open');
      flyStar(btn);
      toast(STR.added + (data && data.title ? ': ' + data.title : ''));
      if (GDC.cartType === 'drawer' && Cart.drawer()) setTimeout(() => Cart.open(), 350);
    } catch (error) {
      if (err) err.textContent = error.message; else toast(error.message);
    } finally {
      if (label) label.classList.remove('is-loading');
      if (btn) btn.removeAttribute('aria-busy');
    }
  });

  /* ---------------- section initialisers ---------------- */
  const inits = {};
  let ready = false;
  const readyQueue = [];
  const whenReady = (fn) => (ready ? fn() : readyQueue.push(fn));

  inits.promo = (el) => {
    const mq = $('[data-mq]', el);
    if (mq) addMarquee(mq, parseFloat(mq.dataset.mq));
  };
  inits.belt = (el) => {
    const mq = $('[data-mq]', el);
    if (mq) addMarquee(mq, parseFloat(mq.dataset.mq), { skew: mq.hasAttribute('data-skew') });
  };

  inits.awning = (el) => {
    whenReady(() => { const g = gs(); if (g && MOTION) g.from(el, { scaleY: 0, transformOrigin: 'top', duration: 1, ease: 'elastic.out(1,.6)' }); });
  };
  inits.nav = (el) => {
    whenReady(() => { const g = gs(); if (g && MOTION) g.from(el, { y: -120, duration: 1.1, ease: 'bounce.out', delay: 0.25, clearProps: 'transform' }); });
  };

  inits.hero = (el) => {
    const g = gs();
    if (FINE && g) {
      el.addEventListener('mousemove', (e) => {
        const px = e.clientX / window.innerWidth - 0.5;
        const py = e.clientY / window.innerHeight - 0.5;
        $$('[data-depth]', el).forEach((n) => g.to(n, { x: px * +n.dataset.depth * 2, y: py * +n.dataset.depth * 2, duration: 1, ease: 'power3.out' }));
      });
    }
    if (hasST() && MOTION) {
      const arch = $('.gd-arch--1 img', el);
      if (arch) g.to(arch, { yPercent: 12, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true } });
      g.to($('[data-hero-media]', el), { y: -60, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true } });
      g.to($('[data-hero-col]', el), { y: 40, ease: 'none', scrollTrigger: { trigger: el, start: 'top top', end: 'bottom top', scrub: true } });
    }
    whenReady(() => {
      const gg = gs();
      if (!gg || !MOTION) return;
      const tl = gg.timeline({ defaults: { ease: 'power4.out' }, delay: 0.3 });
      const q = (s) => $$(s, el);
      if (q('[data-hl="g"]').length) {
        tl.from(q('[data-hl="g"]'), { yPercent: 110, duration: 1.1 }, 0)
          .from(q('[data-hl="d"]'), { yPercent: 110, duration: 1.1 }, 0.14)
          .from(q('[data-hl="s"]'), { scale: 0, rotation: -220, duration: 1.4, ease: 'elastic.out(1,.55)' }, 0.55)
          .from(q('[data-hl="c"]'), { clipPath: 'inset(86% 100% 0 0)', duration: 1, ease: 'power3.inOut' }, 0.75);
      } else if (q('[data-hero-title]').length) {
        const title = q('[data-hero-title]')[0];
        splitWords(title);
        tl.from($$('.wi', title), { yPercent: 115, rotation: 4, stagger: 0.08, duration: 1.1 }, 0);
      } else if (q('[data-hero-logo]').length) {
        tl.from(q('[data-hero-logo]'), { y: 40, opacity: 0, duration: 1 }, 0);
      }
      tl.from(q('[data-hero-tag]'), { y: 40, opacity: 0, rotation: 4, duration: 0.9 }, 1)
        .from(q('[data-hero-lead]'), { y: 30, opacity: 0, duration: 0.9 }, 1.1)
        .from(q('[data-hero-ctas] .gd-btn'), { y: 30, opacity: 0, stagger: 0.1, duration: 0.8 }, 1.2)
        .from(q('.gd-arch'), { clipPath: 'inset(100% 0 0 0)', stagger: 0.14, duration: 1.3, ease: 'power4.inOut' }, 0.5)
        .from(q('.gd-arch img'), { scale: 1.5, stagger: 0.14, duration: 1.8, ease: 'power3.out' }, 0.5)
        .from(q('.gd-sticker'), { scale: 0, rotation: -30, stagger: 0.1, duration: 0.7, ease: 'back.out(2.4)' }, 1.4)
        .from(q('[data-hero-badge]'), { scale: 0, rotation: -180, duration: 1.1, ease: 'back.out(1.6)' }, 1.3)
        .from(q('.gd-scrollcue'), { opacity: 0, x: -20, duration: 0.8 }, 1.6);
    });
  };

  inits.schools = (el) => {
    const track = $('[data-pen-track]', el);
    const pens = $$('.gd-pen', el);
    const pinned = el.hasAttribute('data-pinned');
    const slider = el.hasAttribute('data-slider');
    if (!pinned || !track) return;
    // the arrow slider scrolls an inner element; every other layout scrolls the section itself
    const scrollEl = $('[data-pen-scroll]', el) || el;
    // prev / next arrow buttons (slider layout)
    const arrows = $$('[data-pen-arrow]', el);
    if (arrows.length) {
      const step = () => Math.max(220, scrollEl.clientWidth * 0.8);
      const updateArrows = () => {
        const max = scrollEl.scrollWidth - scrollEl.clientWidth - 2;
        arrows.forEach((a) => { a.disabled = a.dataset.penArrow === 'next' ? scrollEl.scrollLeft >= max : scrollEl.scrollLeft <= 2; });
      };
      arrows.forEach((a) => a.addEventListener('click', () => {
        scrollEl.scrollBy({ left: (a.dataset.penArrow === 'next' ? 1 : -1) * step(), behavior: REDUCED ? 'auto' : 'smooth' });
      }));
      scrollEl.addEventListener('scroll', updateArrows, { passive: true });
      window.addEventListener('resize', () => setTimeout(updateArrows, 150));
      setTimeout(updateArrows, 60);
    }
    const svg = $('[data-pen-string]', el);
    const buildString = () => {
      if (!svg || !pens.length) return;
      const w = track.scrollWidth;
      svg.setAttribute('viewBox', `0 0 ${w} 150`);
      svg.style.width = w + 'px';
      const y = Math.round(pens[0].offsetTop) - 2;
      const xs = pens.map((p) => p.offsetLeft + p.offsetWidth / 2);
      let d = `M0 ${y - 6}`;
      let px = 0;
      xs.forEach((x) => { d += `Q${(px + x) / 2} ${y + 22} ${x} ${y}`; px = x; });
      d += `Q${(px + w) / 2} ${y + 22} ${w} ${y - 6}`;
      const esp = getComputedStyle(document.documentElement).getPropertyValue('--esp');
      svg.innerHTML = `<path d="${d}" fill="none" stroke="${esp}" stroke-width="3.5" stroke-linecap="round"/><path d="${d}" fill="none" stroke="#FFF9F0" stroke-width="3.5" stroke-dasharray="2 8" opacity=".7"/>`;
    };
    let swing = 0;
    let lastX = 0;
    let hVel = 0;
    const isStatic = () => slider || el.classList.contains('is-static');
    tickers.push((t) => {
      if (!el.isConnected || REDUCED) return;
      // vertical scroll speed (pinned) or sideways scroll speed (slider / touch strip) makes the pennants swing
      let v = vel;
      if (isStatic()) { const x = scrollEl.scrollLeft; hVel += (x - lastX - hVel) * 0.2; lastX = x; v = hVel; }
      const target = clamp(-v * 0.55, -13, 13);
      swing += (target - swing) * 0.1;
      pens.forEach((p, i) => { p.style.rotate = (swing * (1 + (i % 4) * 0.18) + Math.sin(t * 1.3 + i * 0.9) * 1.1).toFixed(2) + 'deg'; });
    });
    // touch screens and tablets get a swipeable strip; desktop mice get the pinned sideways scroll
    const canPin = hasST() && !REDUCED && FINE && window.innerWidth > 1000 && !slider;
    if (!canPin) {
      if (!slider) el.classList.add('is-static');
      const hint = $('.gd-pen-hint', el);
      if (hint && hint.dataset.swipeLabel) {
        const svgIcon = $('svg', hint);
        hint.textContent = '';
        if (svgIcon) hint.append(svgIcon);
        hint.append(document.createTextNode(hint.dataset.swipeLabel));
      }
      buildString();
      window.addEventListener('resize', () => setTimeout(buildString, 200));
      window.addEventListener('load', buildString);
      return;
    }
    const g = window.gsap;
    const dist = () => Math.max(0, track.scrollWidth - window.innerWidth);
    const prog = $('[data-pen-prog]', el);
    g.to(track, {
      x: () => -dist(),
      ease: 'none',
      scrollTrigger: {
        trigger: el, start: 'top top', end: () => '+=' + dist(), pin: true, scrub: 0.6, invalidateOnRefresh: true, anticipatePin: 1,
        onRefresh: buildString,
        onUpdate: (st) => { if (prog) prog.style.transform = `scaleX(${st.progress})`; }
      }
    });
    if (MOTION) g.from(pens, { y: -260, opacity: 0, stagger: 0.07, duration: 1.2, ease: 'bounce.out', clearProps: 'opacity', scrollTrigger: { trigger: el, start: 'top 70%', once: true } });
    buildString();
  };

  inits.lab = (el) => {
    const neck = $('[data-lab-neck]', el);
    const charms = $('[data-lab-charms]', el);
    const anchor = $('[data-lab-anchor]', el);
    const input = $('[data-lab-letters]', el);
    const form = $('[data-lab-form]', el);
    const sumLetters = $('[data-lab-sumletters]', el);
    if (!neck || !charms || !anchor) return;
    const max = input ? +input.getAttribute('maxlength') || 6 : 6;
    const NS = 'http://www.w3.org/2000/svg';
    const gold = charms.dataset.gold;
    const shadow = charms.dataset.shadow;
    // bottom-centre of the inner wrap is where the charms hang
    const total = anchor.getTotalLength();
    let bottomLen = 0;
    let maxY = -Infinity;
    for (let i = 0; i <= 400; i++) {
      const l = (total * i) / 400;
      const pt = anchor.getPointAtLength(l);
      if (pt.y > maxY) { maxY = pt.y; bottomLen = l; }
    }
    // which way the path runs at the bottom, so letters always read left to right
    const pa = anchor.getPointAtLength(bottomLen);
    const pb = anchor.getPointAtLength((bottomLen + 2) % total);
    const dir = pb.x >= pa.x ? 1 : -1;
    let prev = '';
    const render = () => {
      let txt = '';
      if (input) {
        txt = input.value.toUpperCase().replace(/[^A-Z0-9&]/g, '').slice(0, max);
        if (txt !== input.value) input.value = txt;
      }
      if (sumLetters) sumLetters.textContent = txt || '—';
      // nothing typed yet: show the placeholder letters faded on the preview
      const ghost = !txt && input && input.dataset.ghost ? input.dataset.ghost.toUpperCase().replace(/[^A-Z0-9&]/g, '').slice(0, max) : '';
      charms.classList.toggle('is-ghost', !!ghost);
      if (ghost) txt = ghost;
      charms.innerHTML = '';
      const n = txt.length;
      // long names: tighter spacing and smaller charms so they stay on the front of the cord
      const spacing = n > 6 ? Math.max(24, 240 / (n - 1)) : n > 5 ? 37 : n > 4 ? 40 : 44;
      const k = n > 6 ? spacing / 37 : 1;
      if (input) input.style.fontSize = n > 7 ? `calc(clamp(2.4rem, 4.4vw, 4rem) * ${(7 / n).toFixed(3)})` : '';
      [...txt].forEach((ch, i) => {
        const len = (bottomLen + dir * (i - (n - 1) / 2) * spacing + total) % total;
        const p = anchor.getPointAtLength(len);
        const p2 = anchor.getPointAtLength((len + 1) % total);
        let ang = (Math.atan2(p2.y - p.y, p2.x - p.x) * 180) / Math.PI;
        if (ang > 90 || ang < -90) ang += 180;
        const g = document.createElementNS(NS, 'g');
        g.setAttribute('transform', `translate(${p.x.toFixed(1)} ${p.y.toFixed(1)}) rotate(${ang.toFixed(1)})${k < 1 ? ` scale(${k.toFixed(3)})` : ''}`);
        const inner = document.createElementNS(NS, 'g');
        inner.setAttribute('class', 'charm-g' + (i >= prev.length || prev[i] !== ch ? ' pop' : ''));
        inner.style.animationDelay = (i * 0.04) + 's';
        const ring = document.createElementNS(NS, 'circle');
        ring.setAttribute('class', 'charm-ring');
        ring.setAttribute('r', '6');
        ring.setAttribute('cy', '2');
        ring.setAttribute('stroke', gold);
        const t = document.createElementNS(NS, 'text');
        t.setAttribute('class', 'charm');
        t.setAttribute('y', '31');
        t.setAttribute('fill', gold);
        t.setAttribute('filter', shadow);
        t.textContent = ch;
        inner.append(ring, t);
        g.append(inner);
        charms.append(g);
      });
      prev = txt;
    };
    if (input) input.addEventListener('input', render);
    render();

    const swatches = $$('[data-lab-swatch]', el);
    const idInput = $('[data-lab-variant]', el);
    const btn = $('[data-lab-submit]', el);
    const btnLabel = btn && $('[data-btn-label]', btn);
    const btnText = btnLabel ? btnLabel.textContent : '';
    const select = (sw) => {
      swatches.forEach((x) => x.setAttribute('aria-pressed', x === sw ? 'true' : 'false'));
      if (idInput) idInput.value = sw.dataset.variantId;
      $$('[data-lab-cname], [data-lab-cname2]', el).forEach((n) => { n.textContent = sw.dataset.name; });
      const now = $('[data-lab-now]', el);
      const was = $('[data-lab-was]', el);
      const xc = form ? extraLetters(form) : { count: 0 };
      const price = formatMoney(+sw.dataset.price + (xc.count ? xc.count * xc.price : 0));
      if (now) now.textContent = price;
      const sum = $('[data-lab-sumprice]', el);
      if (sum) sum.textContent = price;
      if (was) {
        const c = +sw.dataset.compare;
        was.hidden = !(c > +sw.dataset.price);
        if (!was.hidden) was.textContent = formatMoney(c);
      }
      const avail = sw.dataset.available === 'true';
      if (btn) btn.disabled = !avail;
      if (btnLabel) btnLabel.textContent = avail ? btnText : STR.soldOut;
      const color = getComputedStyle(sw).getPropertyValue('--c').trim();
      neck.style.setProperty('--cord', color);
      const dot = $('[data-lab-chipdot]', el);
      if (dot) dot.style.setProperty('--c', color);
    };
    swatches.forEach((sw) => sw.addEventListener('click', () => select(sw)));
    if (form) form.addEventListener('extra:change', () => { const cur = swatches.find((x) => x.getAttribute('aria-pressed') === 'true'); if (cur) select(cur); });
    if (form) {
      form.addEventListener('submit', (e) => {
        if (input && input.required && !input.value.trim()) {
          e.preventDefault();
          const err = $('[data-form-error]', el);
          if (err) err.textContent = STR.lettersRequired;
          input.focus();
        }
      }, true);
    }
    const g = gs();
    if (g && MOTION) {
      const tails = $('[data-lab-tails]', el);
      if (tails) g.to(tails, { rotation: 1.6, svgOrigin: '300 438', duration: 2.8, yoyo: true, repeat: -1, ease: 'sine.inOut' });
      if (hasST()) {
        g.from($('.gd-studio', el), { y: 60, opacity: 0, duration: 1.1, ease: 'power4.out', clearProps: 'opacity,transform', scrollTrigger: { trigger: el, start: 'top 70%', once: true } });
        g.from($$('.gd-flatlay__cord use, .gd-flatlay__tex use, .gd-flatlay__gloss use, .gd-flatlay__shadow use', neck), { strokeDasharray: 1400, strokeDashoffset: 1400, duration: 2, ease: 'power2.inOut', stagger: 0.03, clearProps: 'strokeDasharray,strokeDashoffset', scrollTrigger: { trigger: neck, start: 'top 75%', once: true } });
      }
    }
  };

  inits.lineup = (el) => {
    const grid = $('[data-lineup-grid]', el);
    const empty = $('[data-lineup-empty]', el);
    if (!grid) return;
    const chips = $$('[data-filter]', el);
    const moreWrap = $('[data-lineup-showmore]', el);
    const moreLink = $('[data-lineup-more]', el);
    const STEP = 12;
    const initial = parseInt(grid.dataset.initial, 10) || 48;
    const allCards = () => $$('[data-card]', grid);
    let currentFilter = 'all';
    // how many products the "All" view shows before "Show more"
    const baseVisible = () => (window.innerWidth <= 1000 ? Math.min(initial, STEP) : initial);
    let visible = baseVisible();

    const render = () => {
      const matching = [];
      allCards().forEach((c) => {
        const filterOnly = c.hasAttribute('data-filter-only');
        const on = currentFilter === 'all' ? !filterOnly : (c.dataset.collections || '').includes(' ' + currentFilter + ' ');
        if (on) { matching.push(c); } else { c.hidden = true; c.classList.remove('is-extra'); }
      });
      matching.forEach((c, i) => { c.hidden = false; c.classList.toggle('is-extra', currentFilter === 'all' && i >= visible); });
      const remaining = currentFilter === 'all' ? Math.max(0, matching.length - visible) : 0;
      if (moreWrap) {
        moreWrap.hidden = remaining === 0;
        const cnt = $('[data-showmore-count]', moreWrap);
        if (cnt) cnt.textContent = remaining ? `(${remaining})` : '';
      }
      if (empty) empty.hidden = matching.length > 0;
    };

    if (moreWrap) {
      $('button', moreWrap).addEventListener('click', () => { visible += STEP; render(); if (window.ScrollTrigger) window.ScrollTrigger.refresh(); });
    }
    let rT;
    window.addEventListener('resize', () => { clearTimeout(rT); rT = setTimeout(() => { if (currentFilter === 'all') visible = Math.max(visible, baseVisible()); render(); }, 200); });

    render();

    if (!chips.length) return;
    chips.forEach((chip) => chip.addEventListener('click', () => {
      currentFilter = chip.dataset.filter;
      chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
      visible = baseVisible();
      const state = window.Flip && !REDUCED ? window.Flip.getState(allCards()) : null;
      render();
      if (moreLink) {
        if (!moreLink.dataset.defaultHref) moreLink.dataset.defaultHref = moreLink.getAttribute('href');
        const lbl = $('[data-more-label]', moreLink);
        if (currentFilter === 'all') { moreLink.setAttribute('href', moreLink.dataset.defaultHref); if (lbl) lbl.textContent = moreLink.dataset.defaultLabel; }
        else { moreLink.setAttribute('href', chip.dataset.url); if (lbl) lbl.textContent = (moreLink.dataset.schoolLabel || '[school]').replace('[school]', chip.dataset.label); }
      }
      if (state) {
        window.Flip.from(state, {
          duration: 0.7, ease: 'power3.inOut', stagger: 0.02,
          onEnter: (els) => window.gsap.fromTo(els, { opacity: 0, scale: 0.85 }, { opacity: 1, scale: 1, duration: 0.5, stagger: 0.03 }),
          onLeave: (els) => window.gsap.to(els, { opacity: 0, scale: 0.85, duration: 0.25 })
        });
      }
      if (window.ScrollTrigger) setTimeout(() => window.ScrollTrigger.refresh(), 750);
    }));
  };

  inits.fav = (el) => {
    const g = gs();
    if (!hasST() || !MOTION) return;
    $$('.num', el).forEach((n) => g.to(n, { y: -60, ease: 'none', scrollTrigger: { trigger: n, start: 'top bottom', end: 'bottom top', scrub: true } }));
  };

  inits.story = (el) => {
    const counters = $$('[data-count]', el);
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        io.unobserve(en.target);
        const node = en.target;
        const to = +node.dataset.count || 0;
        const suffix = node.dataset.suffix || '';
        if (REDUCED) { node.textContent = to + suffix; return; }
        const start = performance.now();
        const dur = 2000;
        const step = (now) => {
          const p = Math.min(1, (now - start) / dur);
          node.textContent = Math.round(to * (1 - Math.pow(1 - p, 3))) + suffix;
          if (p < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      });
    }, { threshold: 0.4 });
    counters.forEach((c) => io.observe(c));
    const g = gs();
    if (!hasST() || !MOTION) return;
    $$('[data-step]', el).forEach((st) => {
      const paths = $$('.dr', st);
      paths.forEach((p) => { const L = p.getTotalLength ? p.getTotalLength() : 400; p.style.strokeDasharray = L; p.style.strokeDashoffset = L; });
      g.to(paths, { strokeDashoffset: 0, duration: 1.6, stagger: 0.18, ease: 'power2.inOut', scrollTrigger: { trigger: st, start: 'top 80%', once: true } });
    });
    $$('.gd-sb', el).forEach((sb, i) => g.from(sb, { y: 50, opacity: 0, duration: 0.9, delay: i * 0.08, ease: 'power4.out', clearProps: 'opacity', scrollTrigger: { trigger: sb, start: 'top 90%', once: true } }));
  };

  // video modal (testimonials)
  let modal = null;
  function ensureModal() {
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'gd-vmodal';
    modal.setAttribute('data-lenis-prevent', '');
    modal.setAttribute('role', 'dialog');
    modal.setAttribute('aria-modal', 'true');
    modal.innerHTML = '<div class="gd-vmodal__box"><button type="button" class="gd-vmodal__close" aria-label="Close">×</button><div class="gd-vmodal__in" style="width:100%;height:100%;display:grid;place-items:center"></div></div>';
    document.body.append(modal);
    modal.addEventListener('click', (e) => { if (e.target === modal || e.target.closest('.gd-vmodal__close')) closeModal(); });
    return modal;
  }
  function closeModal() {
    if (!modal || !modal.classList.contains('is-open')) return;
    modal.classList.remove('is-open');
    $('.gd-vmodal__in', modal).innerHTML = '';
    lockScroll(false);
  }
  inits.reviews = (el) => {
    el.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-video-open]');
      if (!btn) return;
      const tpl = $(`template[data-video-tpl="${btn.dataset.videoOpen}"]`, el);
      const m = ensureModal();
      const box = $('.gd-vmodal__box', m);
      const inner = $('.gd-vmodal__in', m);
      inner.innerHTML = '';
      box.classList.remove('gd-vmodal__box--tall');
      if (tpl) inner.append(tpl.content.cloneNode(true));
      const video = $('video', inner);
      if (video) {
        video.addEventListener('loadedmetadata', () => { if (video.videoHeight > video.videoWidth) box.classList.add('gd-vmodal__box--tall'); });
        const p = video.play && video.play();
        if (p && p.catch) p.catch(() => {});
      }
      m.classList.add('is-open');
      lockScroll(true);
      const g = gs();
      if (g) g.fromTo(box, { scale: 0.85, opacity: 0 }, { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.6)' });
      $('.gd-vmodal__close', m).focus();
    });
  };

  inits.gallery = (el) => {
    $$('[data-mq-row]', el).forEach((row) => addMarquee(row, parseFloat(row.dataset.mqRow), { row: true }));
    const g = gs();
    if (!hasST() || !MOTION) return;
    $$('.gd-arch', el).forEach((a, i) => g.fromTo(a, { y: i % 2 ? 30 : -30 }, { y: i % 2 ? -30 : 30, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom top', scrub: true } }));
  };

  function confetti(src) {
    const g = gs();
    if (!g || !src) return;
    const r = src.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 2;
    const cols = ['var(--blue)', 'var(--rust)', 'var(--esp)', 'var(--gold)', 'var(--cream)'];
    for (let i = 0; i < 34; i++) {
      const s = document.createElement('span');
      s.className = 'gd-conf';
      s.style.color = cols[i % cols.length];
      const sz = g.utils.random(14, 30);
      s.style.width = s.style.height = sz + 'px';
      document.body.append(s);
      const a = g.utils.random(-Math.PI, 0);
      const v = g.utils.random(160, 520);
      g.set(s, { x: cx, y: cy });
      g.to(s, { x: cx + Math.cos(a) * v, y: cy + Math.sin(a) * v, rotation: g.utils.random(-360, 360), duration: 0.7, ease: 'power3.out' });
      g.to(s, { y: '+=500', opacity: 0, duration: 1.1, delay: 0.7, ease: 'power2.in', onComplete: () => s.remove() });
    }
  }

  inits.custom = (el) => {
    const input = $('[data-ticket-input]', el);
    const big = $('[data-ticket-big]', el);
    const placeholder = big ? big.textContent.trim() : '';
    if (input && big) {
      input.addEventListener('input', () => {
        const v = input.value.trim() || placeholder;
        big.innerHTML = '';
        [...v].forEach((ch) => { const s = document.createElement('span'); s.textContent = ch === ' ' ? ' ' : ch; big.append(s); });
        const g = gs();
        if (g) g.from(big.children, { y: -26, opacity: 0, rotation: () => g.utils.random(-14, 14), duration: 0.5, ease: 'back.out(2.4)', stagger: 0.012 });
      });
    }
    const ok = $('[data-ticket-success]', el);
    if (ok) whenReady(() => setTimeout(() => confetti(ok), 400));
    const g = gs();
    if (hasST() && MOTION) g.from($('[data-ticket]', el), { y: 120, rotation: -6, opacity: 0, duration: 1.3, ease: 'back.out(1.3)', clearProps: 'opacity', scrollTrigger: { trigger: el, start: 'top 80%', once: true } });
  };

  inits.footer = (el) => {
    const g = gs();
    if (!hasST() || !MOTION) return;
    const star = $('[data-fstar]', el);
    if (star) g.to(star, { rotation: 360, ease: 'none', scrollTrigger: { trigger: el, start: 'top bottom', end: 'bottom bottom', scrub: true } });
    const logo = $('.gd-logo', el);
    if (logo) g.from(logo, { clipPath: 'inset(0 100% 0 0)', duration: 1.6, ease: 'power4.inOut', scrollTrigger: { trigger: el, start: 'top 80%', once: true } });
  };

  /* product page */
  inits.product = (el) => {
    const jsonEl = $('[data-product-json]', el);
    if (!jsonEl) return;
    let product;
    try { product = JSON.parse(jsonEl.textContent); } catch (e) { return; }
    const form = $('[data-product-form]', el);
    const idInput = form && $('[data-variant-id]', form);
    const picker = $('[data-variant-picker]', el);
    const slides = $$('.gd-gallery__slide', el);
    const thumbs = $$('[data-thumb]', el);

    const showMedia = (id) => {
      id = String(id);
      let found = false;
      slides.forEach((s) => {
        const on = s.dataset.mediaId === id;
        if (on) found = true;
        s.classList.toggle('is-active', on);
        if (!on) $$('video', s).forEach((v) => v.pause());
      });
      if (!found && slides[0]) slides[0].classList.add('is-active');
      thumbs.forEach((t) => t.setAttribute('aria-current', t.dataset.thumb === id ? 'true' : 'false'));
    };
    thumbs.forEach((t) => t.addEventListener('click', () => showMedia(t.dataset.thumb)));

    // swipe between photos on touch screens
    const main = $('.gd-gallery__main', el);
    if (main && slides.length > 1) {
      let sx = null;
      main.addEventListener('touchstart', (e) => { sx = e.touches[0].clientX; }, { passive: true });
      main.addEventListener('touchend', (e) => {
        if (sx === null) return;
        const dx = e.changedTouches[0].clientX - sx;
        sx = null;
        if (Math.abs(dx) < 40) return;
        const idx = slides.findIndex((s) => s.classList.contains('is-active'));
        const next = slides[(idx + (dx < 0 ? 1 : -1) + slides.length) % slides.length];
        showMedia(next.dataset.mediaId);
      });
    }

    // zoom
    const lb = $('[data-lightbox]', el);
    const zoom = $('[data-zoom]', el);
    if (zoom && lb) {
      zoom.addEventListener('click', () => {
        const img = $('.gd-gallery__slide.is-active img', el);
        if (!img) return;
        const set = (img.getAttribute('srcset') || '').split(',').map((s) => s.trim().split(' ')[0]).filter(Boolean);
        const holder = $('[data-lightbox-holder]', lb);
        if (holder) holder.innerHTML = `<img src="${set.length ? set[set.length - 1] : img.currentSrc || img.src}" alt="${(img.alt || '').replace(/"/g, '&quot;')}">`;
        lb.classList.add('is-open');
        lb.setAttribute('aria-hidden', 'false');
        lockScroll(true);
      });
      lb.addEventListener('click', () => { lb.classList.remove('is-open'); lb.setAttribute('aria-hidden', 'true'); lockScroll(false); });
    }

    const priceEl = $('[data-price]', el);
    const renderPrice = (v) => {
      if (!priceEl) return;
      let html = `<span class="now">${formatMoney(v.price)}</span>`;
      if (v.compare_at_price && v.compare_at_price > v.price) {
        const pct = Math.floor(((v.compare_at_price - v.price) * 100) / v.compare_at_price);
        html += `<s>${formatMoney(v.compare_at_price)}</s><span class="gd-badge">${(STR.save || 'Save [percent]%').replace('[percent]', pct)}</span>`;
      }
      priceEl.innerHTML = html;
    };

    const selected = () => $$('fieldset[data-option-index]', picker).map((fs) => { const c = $('input:checked', fs); return c ? c.value : null; });
    const findVariant = (opts) => product.variants.find((v) => v.options.every((o, i) => o === opts[i]));
    const btn = $('[data-add-btn]', el);
    const btnLabel = btn && $('[data-btn-label]', btn);

    const update = (initial) => {
      if (!picker) return;
      const opts = selected();
      $$('fieldset[data-option-index]', picker).forEach((fs, i) => {
        const lab = $('[data-option-value]', fs);
        if (lab) lab.textContent = opts[i] || '';
        $$('input', fs).forEach((inp) => {
          const test = opts.slice();
          test[i] = inp.value;
          const m = findVariant(test);
          inp.classList.toggle('is-unavailable', !m || !m.available);
        });
      });
      const v = findVariant(opts);
      if (!v) {
        if (btn) { btn.disabled = true; if (btnLabel) btnLabel.textContent = STR.unavailable; }
        return;
      }
      if (initial) return;
      if (idInput) { idInput.value = v.id; idInput.dispatchEvent(new Event('change', { bubbles: true })); }
      if (btn) { btn.disabled = !v.available; if (btnLabel) btnLabel.textContent = v.available ? STR.addToCart : STR.soldOut; }
      renderPrice(v);
      if (v.featured_media) showMedia(v.featured_media.id);
      if (el.dataset.updateUrl === 'true') window.history.replaceState({}, '', `${el.dataset.productUrl}?variant=${v.id}`);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant: v, product } }));
    };
    if (picker) {
      picker.addEventListener('change', () => update(false));
      update(true);
    }

    $$('[data-copy-link]', el).forEach((b) => b.addEventListener('click', () => {
      const url = b.dataset.copyLink;
      if (navigator.clipboard) navigator.clipboard.writeText(url).then(() => toast(STR.copied));
    }));
  };

  // built-in personalization field (letters → live gold charm preview)
  function initPersonal(root) {
    $$('[data-personal]', root).forEach((box) => {
      if (box.dataset.ready) return;
      box.dataset.ready = '1';
      const input = $('[data-personal-input]', box);
      const count = $('[data-personal-count]', box);
      const letters = $('[data-charm-letters]', box);
      const preview = $('[data-charm-preview]', box);
      if (!input) return;
      const ph = letters ? letters.innerHTML : '';
      let prev = '';
      const render = () => {
        const max = +input.getAttribute('maxlength') || 6;
        const v = input.value.toUpperCase().replace(/[^A-Z0-9&'.-]/g, '').slice(0, max);
        if (v !== input.value) input.value = v;
        if (count) count.textContent = v.length;
        if (!letters) return;
        if (!v) { letters.innerHTML = ph; prev = ''; return; }
        letters.innerHTML = '';
        [...v].forEach((ch, i) => {
          const sp = document.createElement('span');
          sp.textContent = ch === ' ' ? '\u00a0' : ch;
          if (i >= prev.length || prev[i] !== ch) sp.classList.add('pop');
          letters.append(sp);
        });
        prev = v;
      };
      input.addEventListener('input', render);
      render();
      const setCord = () => {
        if (!preview) return;
        const checked = $('[data-variant-picker] input:checked + label .gd-dot', box.closest('[data-gd="product"]') || document);
        if (checked) preview.style.setProperty('--cord', getComputedStyle(checked).getPropertyValue('--c').trim());
      };
      setCord();
      document.addEventListener('variant:change', setCord);
    });
  }

  inits.recs = (el) => {
    const url = el.dataset.url;
    if (!url || el.children.length) return;
    fetch(url).then((r) => r.text()).then((html) => {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = $('[data-gd="recs"]', doc);
      if (fresh && fresh.innerHTML.trim()) {
        el.innerHTML = fresh.innerHTML;
        initMagnetic(el);
        initTilt(el);
        revealIn(el);
        if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      }
    }).catch(() => {});
  };

  inits.search = (el) => inits.collection(el);
  inits.collection = (el) => {
    const form = $('[data-facets]', el);
    if (!form) return;
    form.addEventListener('change', (e) => {
      if (e.target.matches('[data-facet-input]')) form.requestSubmit ? form.requestSubmit() : form.submit();
    });
    form.addEventListener('submit', () => {
      $$('input[type=number]', form).forEach((i) => { if (!i.value) i.disabled = true; });
    });
    document.addEventListener('click', (e) => {
      $$('details.gd-filter[open]', el).forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
  };

  inits.login = (el) => {
    const login = $('#GdLogin', el);
    const recover = $('#recover', el);
    const toggle = () => { login.hidden = !login.hidden; recover.hidden = !recover.hidden; };
    $$('[data-toggle-recover]', el).forEach((a) => a.addEventListener('click', (e) => { e.preventDefault(); toggle(); }));
    if (window.location.hash === '#recover' || $('[data-recover-success]', el)) { login.hidden = true; recover.hidden = false; }
  };

  inits.addresses = (el) => {
    $$('form[data-confirm]', el).forEach((f) => f.addEventListener('submit', (e) => { if (!window.confirm(f.dataset.confirm)) e.preventDefault(); }));
    window.addEventListener('load', () => {
      if (!window.Shopify || !window.Shopify.CountryProvinceSelector) return;
      $$('[data-country-select]', el).forEach((sel) => {
        const s = sel.dataset.countrySelect;
        new window.Shopify.CountryProvinceSelector(`AddressCountry${s}`, `AddressProvince${s}`, { hideElement: `AddressProvinceContainer${s}` });
      });
    });
  };

  function initSection(root) {
    const els = $$('[data-gd]', root);
    if (root.matches && root.matches('[data-gd]')) els.unshift(root);
    els.forEach((el) => {
      if (el.__gdInit) return;
      el.__gdInit = true;
      const fn = inits[el.dataset.gd];
      if (fn) {
        try { fn(el); } catch (err) { console.error('[GDC]', el.dataset.gd, err); }
      }
    });
    initMagnetic(root);
    initTilt(root);
    initPersonal(root);
    initExtraLetters(root);
    revealIn(root);
  }

  /* ---------------- page-long cord + football field progress ---------------- */
  const cordWrap = $('#gd-cord');
  let cord = null;
  function buildCord() {
    if (!cordWrap || REDUCED || window.innerWidth < 700) { if (cordWrap) cordWrap.innerHTML = ''; cord = null; return; }
    const H = document.body.scrollHeight;
    const W = document.documentElement.clientWidth;
    const mainEl = $('#MainContent');
    if (!H || !mainEl) return;
    const m = W < 700 ? 9 : Math.max(16, W * 0.022);
    const L = m;
    const R = W - m;
    const top = (el) => el.getBoundingClientRect().top + window.scrollY;
    const sections = $$('#MainContent > .shopify-section, .shopify-section-group-footer-group');
    if (!sections.length) return;
    const pts = [];
    const push = (x, y) => pts.push([x, y]);
    const run = (x, ya, yb, amp, ph) => { for (let y = ya; y <= yb; y += 26) push(x + Math.sin(y / 150 + ph) * amp, y); };
    const sw = (xa, xb, ya, yb) => { for (let i = 0; i <= 14; i++) { const t = i / 14; const e = t * t * (3 - 2 * t); push(xa + (xb - xa) * e, ya + (yb - ya) * t); } };
    const loopAt = (x, y, dir) => { const a = 13; const b = Math.min(36, W * 0.04); for (let i = 0; i <= 44; i++) { const t = (i / 44) * Math.PI * 2; push(x + dir * b * (1 - Math.cos(t)), y + a * t - b * Math.sin(t)); } return y + a * Math.PI * 2; };
    let side = L;
    let y = top(mainEl) + 20;
    let loops = 0;
    sections.forEach((sec, idx) => {
      const sTop = top(sec);
      const sBot = sTop + sec.offsetHeight;
      if (sBot <= y) return;
      const opaque = !!sec.querySelector('[data-opaque]');
      const last = idx === sections.length - 1;
      if (opaque && !last) {
        const mid = sTop + sec.offsetHeight * 0.5;
        run(side, y, sTop + 10, 5, idx);
        const other = side === L ? R : L;
        sw(side, other, sTop + 10, mid);
        side = other;
        y = mid;
        run(side, y, sBot, 4, idx);
        y = sBot;
      } else if (last) {
        run(side, y, sTop + 60, 4, idx);
        y = sTop + 60;
      } else {
        if (sec.offsetHeight > 700 && loops < 3) {
          const ly = sTop + sec.offsetHeight * 0.38;
          run(side, y, ly, 5, idx);
          y = loopAt(side, ly, side === L ? 1 : -1);
          loops++;
        }
        run(side, y, sBot, 5, idx);
        y = sBot;
      }
    });
    if (pts.length < 3) return;
    let d = `M${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
    for (let i = 1; i < pts.length - 1; i++) {
      const mx = (pts[i][0] + pts[i + 1][0]) / 2;
      const my = (pts[i][1] + pts[i + 1][1]) / 2;
      d += `Q${pts[i][0].toFixed(1)} ${pts[i][1].toFixed(1)} ${mx.toFixed(1)} ${my.toFixed(1)}`;
    }
    const lastPt = pts[pts.length - 1];
    d += `L${lastPt[0].toFixed(1)} ${lastPt[1].toFixed(1)}`;
    const sw2 = W < 700 ? 5 : 7;
    cordWrap.innerHTML = `<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><path data-cp d="${d}" fill="none" stroke="var(--rust)" stroke-width="${sw2}" stroke-linecap="round"/><g data-bead><circle r="19" fill="var(--cream)" stroke="var(--esp)" stroke-width="3"/><path transform="translate(-12 -12) scale(.24)" fill="var(--esp)" d="M50 4l13.2 29.8 32.3 3.4-24.1 21.8 6.9 31.9L50 74.4 31.7 90.9l6.9-31.9L14.5 37.2l32.3-3.4z"/></g></svg>`;
    const p = $('[data-cp]', cordWrap);
    const len = p.getTotalLength();
    const samples = [];
    for (let i = 0; i <= 600; i++) { const l = (len * i) / 600; samples.push([l, p.getPointAtLength(l).y]); }
    p.style.strokeDasharray = `${len} ${len}`;
    cord = { p, bead: $('[data-bead]', cordWrap), len, samples };
    updateCord();
  }
  function updateCord() {
    if (!cord) return;
    const target = window.scrollY + window.innerHeight * 0.62;
    let l = 0;
    for (const s of cord.samples) { l = s[0]; if (s[1] >= target) break; }
    cord.p.style.strokeDashoffset = cord.len - l;
    const pt = cord.p.getPointAtLength(Math.max(l, 0.1));
    cord.bead.setAttribute('transform', `translate(${pt.x} ${pt.y}) rotate(${(l / 18) % 360})`);
  }
  const field = $('[data-field]');
  function updateField() {
    if (!field) return;
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
    const x = 6 + p * 88;
    const mk = $('[data-field-mark]', field);
    const fill = $('[data-field-fill]', field);
    if (mk) { mk.style.left = x + '%'; mk.style.rotate = p * 720 + 'deg'; }
    if (fill) fill.style.width = x + '%';
    field.classList.toggle('end', p > 0.985);
  }
  function onScroll() { onScrollHeader(); updateField(); updateCord(); }

  /* ---------------- opening ticket ---------------- */
  function runPreloader(done) {
    const root = document.documentElement;
    const pre = $('#gd-pre');
    if (!pre || !root.classList.contains('show-pre')) { if (pre) pre.remove(); done(); return; }
    const finish = () => {
      try { sessionStorage.setItem('gd_pre_seen', '1'); } catch (e) {}
      root.classList.remove('show-pre');
      pre.remove();
    };
    const g = window.gsap;
    if (!g || REDUCED) { finish(); done(); return; }
    const count = $('[data-pre-count]', pre);
    const seat = $('[data-pre-seat]', pre);
    const o = { v: 0 };
    g.timeline()
      .from($('.tk', pre), { scale: 0.6, rotation: -8, opacity: 0, duration: 0.9, ease: 'back.out(2)' }, 0)
      .to(o, { v: 100, duration: 1.7, ease: 'power2.inOut', onUpdate: () => { if (count) count.textContent = Math.round(o.v); if (seat) seat.textContent = String(Math.round((o.v / 100) * 26)).padStart(2, '0'); } }, 0.2)
      .to($('.stub', pre), { rotation: 24, x: 30, y: 60, opacity: 0, duration: 0.7, ease: 'power3.in' }, '>0.15')
      .to($('.main', pre), { x: -20, duration: 0.4 }, '<')
      .to($('.tk', pre), { opacity: 0, y: -40, duration: 0.4 }, '>-0.1')
      .to($('.sheet', pre), { yPercent: -104, duration: 1.1, ease: 'power4.inOut' }, '>-0.05')
      .add(done, '<+0.25')
      .add(finish);
  }

  /* ---------------- boot ---------------- */
  function boot() {
    initLenis();
    initCursor();
    initSection(document);
    Cart.reconcile().catch(() => {});
    window.addEventListener('scroll', onScroll, { passive: true });
    if (lenis) lenis.on('scroll', onScroll);
    onScroll();
    runPreloader(() => {
      ready = true;
      readyQueue.splice(0).forEach((fn) => { try { fn(); } catch (e) { console.error(e); } });
      if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      buildCord();
      const g = gs();
      if (g && cordWrap) g.from(cordWrap, { opacity: 0, duration: 1.2, delay: 1.2 });
    });
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); buildCord(); }, 250);
  });
  window.addEventListener('load', () => {
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    buildCord();
    setTimeout(() => { if (window.ScrollTrigger) window.ScrollTrigger.refresh(); buildCord(); }, 900);
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    Cart.close();
    closeMobileNav();
    closeSearch();
    closeModal();
    const lb = $('.gd-lightbox.is-open');
    if (lb) { lb.classList.remove('is-open'); lockScroll(false); }
  });

  /* theme editor support */
  document.addEventListener('shopify:section:load', (e) => {
    initSection(e.target);
    if (window.ScrollTrigger) window.ScrollTrigger.refresh();
    buildCord();
  });
  document.addEventListener('shopify:section:unload', (e) => {
    if (window.ScrollTrigger) window.ScrollTrigger.getAll().forEach((st) => { if (st.trigger && e.target.contains(st.trigger)) st.kill(true); });
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('[data-cart-drawer]')) Cart.open();
  });
  document.addEventListener('shopify:section:deselect', (e) => {
    if (e.target.querySelector('[data-cart-drawer]')) Cart.close();
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
